import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Select from '../components/select/Select';
import Text from '../components/Text';
import { useTemplateHandlers } from '../services/actions/template/useTemplateHandlers';
import {
  getTemplatesSelectCache,
  mapTemplatesToOptions,
  TemplateLibraryOption,
} from '../services/templateLibraryCache';
import { AppRoutes } from '../interfaces/store/appStore';
import { styles } from '../styles/themes/style';

interface ITemplateLibrarySelectProps {
  onInsert: (content: string) => void;
  disabled?: boolean;
  /**
   * 'button' is the compact form used in the campaign composer's action row, where
   * this sits beside "Generate message" and a 360px search select would dominate a
   * row of small controls. Same options, same onInsert.
   */
  variant?: 'select' | 'button';
}

const TemplateLibrarySelect: FunctionComponent<ITemplateLibrarySelectProps> = ({ onInsert, disabled, variant = 'select' }) => {
  const navigate = useNavigate();
  const { getTemplatesSelectHandler } = useTemplateHandlers();
  const getTemplatesRef = useRef(getTemplatesSelectHandler);
  getTemplatesRef.current = getTemplatesSelectHandler;

  const cached = getTemplatesSelectCache();
  const [options, setOptions] = useState<TemplateLibraryOption[]>(() =>
    cached ? mapTemplatesToOptions(cached.templates) : [],
  );
  const [loading, setLoading] = useState(() => !cached);
  const [selected, setSelected] = useState<TemplateLibraryOption | null>(null);

  useEffect(() => {
    const warm = getTemplatesSelectCache();
    if (warm) {
      setOptions(mapTemplatesToOptions(warm.templates));
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await getTemplatesRef.current();
      if (cancelled) return;
      setOptions(mapTemplatesToOptions(result?.templates ?? []));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (variant === 'button') {
    return (
      <TemplateButtonMenu
        options={options}
        loading={loading}
        disabled={disabled}
        onInsert={onInsert}
        onCreate={() => navigate(AppRoutes.TEMPLATES)}
      />
    );
  }

  if (loading) {
    return <Text $paragraph3 $lighter>Loading templates…</Text>;
  }

  if (!options.length) {
    return (
      <EmptyWrap>
        <Text $paragraph3 $lighter>No saved templates yet.</Text>
        <LinkBtn type="button" onClick={() => navigate(AppRoutes.TEMPLATES)}>
          Create templates
        </LinkBtn>
      </EmptyWrap>
    );
  }

  return (
    <SelectWrap>
      <Select
        placeholder="Insert from template library…"
        isSearchable
        isDisabled={disabled}
        value={selected}
        options={options}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        onChange={(opt: any) => {
          if (!opt?.value) return;
          onInsert(String(opt.value));
          setSelected(null);
        }}
      />
    </SelectWrap>
  );
};

/**
 * Compact "Use Template ▾" — a button that drops a short list.
 *
 * The menu is portalled to the body because the composer nests this inside several
 * `overflow: hidden` step cards, which would otherwise clip it.
 */
const TemplateButtonMenu: FunctionComponent<{
  options: TemplateLibraryOption[];
  loading?: boolean;
  disabled?: boolean;
  onInsert: (content: string) => void;
  onCreate: () => void;
}> = ({ options, loading, disabled, onInsert, onCreate }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    const reposition = () => place();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  return (
    <>
      <TplTrigger
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        $open={open}
        onClick={() => { place(); setOpen(o => !o); }}
      >
        {loading ? 'Templates…' : 'Use Template'}
        <TplChevron $open={open}>
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
            <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </TplChevron>
      </TplTrigger>

      {open && createPortal(
        <TplMenu ref={menuRef} style={{ top: pos.top, left: pos.left }}>
          {options.length === 0 ? (
            <TplEmpty>
              No saved templates yet.
              <LinkBtn type="button" onClick={() => { setOpen(false); onCreate(); }}>
                Create templates
              </LinkBtn>
            </TplEmpty>
          ) : options.map(opt => (
            <TplItem
              key={String(opt.value)}
              type="button"
              onClick={() => { onInsert(String(opt.value)); setOpen(false); }}
            >
              {opt.label}
            </TplItem>
          ))}
        </TplMenu>,
        document.body,
      )}
    </>
  );
};

const TplTrigger = styled.button<{ $open: boolean }>`
  display: inline-flex; align-items: center; gap: 7px;
  height: 32px; padding: 0 12px; border-radius: 8px; cursor: pointer;
  border: 1.5px solid ${({ $open }) => ($open ? styles.colors.primary600 : '#e2e8f0')};
  background: #fff;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  color: ${({ $open }) => ($open ? styles.colors.primary600 : '#64748b')};
  white-space: nowrap;
  transition: all 0.12s;
  &:hover:not(:disabled) { border-color: ${styles.colors.primary600}; color: ${styles.colors.primary600}; }
  &:disabled { opacity: .6; cursor: default; }
`;

const TplChevron = styled.span<{ $open: boolean }>`
  display: flex; align-items: center;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s;
  svg { display: block; }
`;

const TplMenu = styled.div`
  position: fixed; z-index: 9999;
  width: 280px; max-height: 300px; overflow-y: auto;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  box-shadow: 0 8px 30px rgba(20,30,80,0.12);
  padding: 6px;
`;

const TplItem = styled.button`
  display: block; width: 100%; text-align: left;
  padding: 8px 10px; border: none; background: none; border-radius: 8px; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; color: #334155;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &:hover { background: #f6f8fc; }
`;

const TplEmpty = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  padding: 10px;
  font-family: NunitoSans; font-size: 12px; color: #94a3b8;
`;

const SelectWrap = styled.div`
  width: 100%;
  max-width: 360px;
`;

const EmptyWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 700;
  color: ${styles.colors.primary600};
  cursor: pointer;
  text-decoration: underline;
`;

export default TemplateLibrarySelect;
